import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../data";
import { useAccount, useStaffWorkspace } from "../../../app/session";
import { ErrorState, Loading } from "../../../components/States";
import { PageHead, PillView, Toolbar, useAction } from "../../../components/ui";
import "./hilfe.css";
import { MeldenKnopf } from "../../../components/Melden";
import { date, time } from "../../../lib/format";
import { t } from "../../../lib/sprache";
import { fehlerText } from "../../../lib/fehler";
import { WEBSITE_URL } from "../../../app/nav";
import {
  ANHANG_TYPEN, MAX_ANHANG_BYTES, anhangHochladen, anhangTypErlaubt, loadEigeneMeldungen, loadMeldung, loadTicket, loadTickets, ticketAnlegen, ticketAntworten, ticketLoesen,
  type Anhang, type TicketKategorie,
} from "./data";
import { groesse, kategorieLabel, meldungStatusPill, ticketNummer, ticketStatusPill } from "./modell";
import { anleitungWeg, hilfeArtikel, passendeArtikel, sucheArtikel, type HilfeArtikel } from "./artikel";
import { ANBIETER } from "../einstellungen/model";
import { Coco } from "../../../components/States";
import { toast } from "../../../components/Toast";
import { useOnboardingPrefs } from "../../creator/Einfuehrung";
import { cocoUiText } from "../coco-rundgang-texte";
import { rolleFuer, rundgangStarten } from "../coco-rundgang";

/**
 * Hilfe & Support (0176): Anleitungen zu den Kernabläufen (durchsuchbar), Tickets der Agentur an den Betrieb, eigene
 * Fehlermeldungen mit Stand, „Fehler melden“ auch ohne Fehlermeldung, die Support-Adresse. Tickets sieht, wer sie angelegt
 * hat, und der Admin der Agentur.
 */
export function Hilfe() {
  const ws = useStaffWorkspace();
  const { account } = useAccount();
  const userId = account?.session.userId ?? "";
  const tickets = useQuery({ queryKey: ["support_tickets", ws.org.org_id], queryFn: () => loadTickets(api, ws.org.org_id) });
  const meldungen = useQuery({ queryKey: ["error_reports", userId], enabled: !!userId, queryFn: () => loadEigeneMeldungen(api, userId) });
  if (tickets.error) return <ErrorState error={tickets.error} retry={() => void tickets.refetch()} />;
  return (
    <main id="main" className="content tk-hilfe">
      <PageHead eyebrow={ws.org.name} title={t("Hilfe & Support", "Help & support")}
        text={<>{t("Fragen, Wünsche und Probleme direkt an das Team von CreatorOffice. Wir antworten per Mail und hier im Portal.",
          "Questions, requests and problems straight to the CreatorOffice team. We reply by email and here in the portal.")}</>}>
        <div className="row">
          <Link className="btn primary" to="/agentur/hilfe/neu">{t("Neues Ticket", "New ticket")}</Link>
          <MeldenKnopf message={t("Fehler gemeldet über Hilfe & Support", "Problem reported via help & support")} className="" />
        </div>
      </PageHead>
      {/* D-1 (Inhaber 21.10.2026): ab breiter Ansicht Tickets und Coco nebeneinander, darunter die Anleitungen */}
      <div className="tk-hilfe-oben">
      <section className="panel stack" aria-labelledby="tickets-titel">
        <div className="row between tk-hilfe-ticketkopf">
          <h2 id="tickets-titel">{t("Meine Tickets", "My tickets")}</h2>
          {/* D-1: die Support-Adresse hier statt am Ende der einzeiligen Beschreibung im Seitenkopf, wo sie abgeschnitten wurde */}
          <small>{t("Per E-Mail erreichst du uns unter ", "You can also email us at ")}<a href={`mailto:${ANBIETER.kontakt}`}>{ANBIETER.kontakt}</a>.</small>
        </div>
        {tickets.isPending ? <Loading /> : tickets.data!.length === 0 ? (
          <p className="tk-empty">{t("Noch keine Tickets. Lege eins an, wenn du Hilfe brauchst.", "No tickets yet. Create one whenever you need help.")}</p>
        ) : (
          <div className="tablewrap">
            <table>
              <caption className="sr-only">{t("Tickets", "Tickets")}</caption>
              <thead><tr><th scope="col">{t("Ticket", "Ticket")}</th><th scope="col">{t("Kategorie", "Category")}</th><th scope="col">{t("Status", "Status")}</th><th scope="col">{t("Zuletzt", "Last update")}</th></tr></thead>
              <tbody>
                {tickets.data!.map((x) => (
                  <tr key={x.id}>
                    <td><Link to={`/agentur/hilfe/tickets/${x.id}`}>{ticketNummer(x.number)} {x.subject}</Link></td>
                    <td>{kategorieLabel(x.category, t)}</td>
                    <td><PillView pill={ticketStatusPill(x.status, t)} /></td>
                    <td>{date(x.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <CocoHilfe userId={userId || null} />
      </div>
      <Anleitungen creator={!!ws.isCreator} />
      {meldungen.data && meldungen.data.length > 0 && (
        <section className="panel stack" aria-labelledby="meldungen-titel">
          <h2 id="meldungen-titel">{t("Meine gemeldeten Fehler", "My reported problems")}</h2>
          <ul className="stack tk-liste-schlicht">
            {meldungen.data.map((m) => (
              <li key={m.id} className="row between">
                <span>{m.message}<small> · {date(m.created_at)}{m.resolved_release ? t(` · behoben in Version ${m.resolved_release}`, ` · fixed in version ${m.resolved_release}`) : ""}</small></span>
                <span className="row">
                  <PillView pill={meldungStatusPill(m.status, t)} />
                  {m.ticket_id ? <Link to={`/agentur/hilfe/tickets/${m.ticket_id}`}>{t("Zum Ticket", "Open ticket")}</Link>
                    : m.status !== "erledigt" && m.status !== "kein_fehler" && <Link to={`/agentur/hilfe/neu?meldung=${m.id}`}>{t("Als Ticket weiterführen", "Continue as a ticket")}</Link>}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}

/** Verweis auf einen Hilfeartikel: Anleitungen auf der Hilfe-Seite, Seiten der App, Ratgeber der Website im neuen Tab */
function ArtikelLink({ a }: { a: HilfeArtikel }) {
  if (a.schritte) return <Link to={anleitungWeg(a.id)}>{a.titel}</Link>;
  return a.extern ? <a href={a.ziel} target="_blank" rel="noreferrer">{a.titel}</a> : <Link to={a.ziel}>{a.titel}</Link>;
}

/**
 * Anleitungen (07.10.2026): Schritt-für-Schritt zu den Kernabläufen, aufklappbar und durchsuchbar. Die Suche findet auch die
 * Erklärungen der Seiten, Einstellungen und Ratgeber; ?anleitung=<id> öffnet eine Anleitung direkt (Vorschläge beim Ticket).
 */
function Anleitungen({ creator }: { creator: boolean }) {
  const [params] = useSearchParams();
  const offen = params.get("anleitung");
  const [suche, setSuche] = useState("");
  const alle = useMemo(() => hilfeArtikel(t, WEBSITE_URL, creator), [creator]);
  const treffer = suche.trim() ? sucheArtikel(alle, suche) : null;
  const liste = (treffer ?? alle).filter((a) => a.schritte);
  const weitere = treffer ? treffer.filter((a) => !a.schritte) : [];
  useEffect(() => {
    if (offen) document.getElementById(`anleitung-${offen}`)?.scrollIntoView({ block: "start" });
  }, [offen]);
  return (
    <section className="panel stack" aria-labelledby="anleitungen-titel">
      <h2 id="anleitungen-titel">{t("Anleitungen", "Guides")}</h2>
      <p>{t("Schritt für Schritt durch die wichtigsten Abläufe. Findest du nicht, was du suchst, schreib uns ein Ticket.", "Step by step through the most important tasks. If you cannot find what you are looking for, send us a ticket.")}</p>
      <Toolbar search={suche} onSearch={setSuche} label={t("Hilfe durchsuchen", "Search help")} placeholder={t("z. B. Rechnung senden, Mahnung, Kündigung", "e.g. send invoice, dunning, cancel")} />
      {liste.length === 0 && weitere.length === 0 && (
        <p className="tk-empty" role="status">{t("Dazu gibt es noch keine Anleitung. Versuch ein anderes Wort oder lege ein Ticket an.", "There is no guide for this yet. Try another word or create a ticket.")}</p>
      )}
      {liste.length > 0 && (
        <div className="tk-anleitungen">
          {liste.map((a) => (
            <details key={a.id} id={`anleitung-${a.id}`} open={a.id === offen || undefined}>
              <summary>{a.titel}</summary>
              <div className="stack">
                <p>{a.text}</p>
                <ol className="tk-anleitung-schritte">
                  {a.schritte!.map((s, i) => (
                    <li key={i}>{s.text}{s.to && <> <Link to={s.to} aria-label={schrittZiel(s.text, t)}>{t("Öffnen", "Open")}</Link></>}</li>
                  ))}
                </ol>
                <div className="row"><Link className="btn" to={a.ziel}>{a.zielText ?? t("Zur Seite", "Go to page")}</Link></div>
              </div>
            </details>
          ))}
        </div>
      )}
      {weitere.length > 0 && (
        <div className="stack" role="status" aria-live="polite">
          <strong>{t("Weitere Treffer", "More results")}</strong>
          <ul className="tk-liste-schlicht">
            {weitere.map((a) => <li key={a.id}><ArtikelLink a={a} /> <small>{a.text}</small></li>)}
          </ul>
        </div>
      )}
    </section>
  );
}

function Anhaenge({ orgId, value, onChange }: { orgId: string; value: Anhang[]; onChange: (a: Anhang[]) => void }) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const hinzu = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    setFehler(null);
    try {
      const neu: Anhang[] = [];
      for (const f of Array.from(files).slice(0, 10 - value.length)) {
        if (f.size > MAX_ANHANG_BYTES) { setFehler(t(`„${f.name}“ ist größer als 10 MB.`, `“${f.name}” is larger than 10 MB.`)); continue; }
        if (!anhangTypErlaubt(f)) { setFehler(t(`„${f.name}“ geht nicht als Anhang. Erlaubt sind Bilder, Bildschirmaufnahmen, PDF, Text und CSV.`, `“${f.name}” cannot be attached. Images, screen recordings, PDF, text and CSV are allowed.`)); continue; }
        neu.push(await anhangHochladen(api, orgId, f));
      }
      onChange([...value, ...neu]);
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="stack">
      <label className="btn" aria-busy={busy}>
        {busy ? t("Wird hochgeladen …", "Uploading …") : t("Dateien anhängen", "Attach files")}
        <input className="sr-only" type="file" multiple accept={ANHANG_TYPEN.join(",")} disabled={busy || value.length >= 10} onChange={(e) => { void hinzu(e.target.files); e.target.value = ""; }} />
      </label>
      {value.length > 0 && (
        <ul className="tk-liste-schlicht">
          {value.map((a) => (
            <li key={a.path} className="row between">
              <span>{a.name} <small>{groesse(a.size)}</small></span>
              <button type="button" className="quiet" onClick={() => onChange(value.filter((x) => x.path !== a.path))}>{t("Entfernen", "Remove")}</button>
            </li>
          ))}
        </ul>
      )}
      <small>{t("Bis zu 10 Dateien, je höchstens 10 MB: Bilder, Bildschirmaufnahmen, PDF, Text oder CSV.", "Up to 10 files, at most 10 MB each: images, screen recordings, PDF, text or CSV.")}</small>
      {fehler && <p className="tk-error" role="alert">{fehler}</p>}
    </div>
  );
}

export function TicketNeu() {
  const ws = useStaffWorkspace();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const meldungId = params.get("meldung");
  const meldung = useQuery({ queryKey: ["error_report", meldungId], enabled: !!meldungId, queryFn: () => loadMeldung(api, meldungId!) });
  const [kategorie, setKategorie] = useState<TicketKategorie>(meldungId ? "fehler" : "frage");
  const [betreff, setBetreff] = useState("");
  const [text, setText] = useState("");
  const [anhaenge, setAnhaenge] = useState<Anhang[]>([]);
  const { busy, error, run } = useAction();
  // Creator-Konto: eigene Fassungen und Erklärungen (studioGuideText), ohne Agentur-Abläufe
  const artikel = useMemo(() => hilfeArtikel(t, WEBSITE_URL, !!ws.isCreator), [ws.isCreator]);
  const vorschlaege = useMemo(() => passendeArtikel(artikel, `${betreff} ${text}`), [artikel, betreff, text]);
  const betreffWert = betreff || (meldung.data ? meldung.data.message.slice(0, 160) : "");
  const senden = (e: FormEvent) => {
    e.preventDefault();
    void run("ticket", () => ticketAnlegen(api, ws.org.org_id, { category: kategorie, subject: betreffWert, body: text, attachments: anhaenge, errorReport: meldungId }),
      t("Ticket gesendet. Wir melden uns per Mail.", "Ticket sent. We will get back to you by email."))
      .then((id) => { if (id) navigate(`/agentur/hilfe/tickets/${id}`); });
  };
  const kategorien: TicketKategorie[] = ["frage", "fehler", "abrechnung", "wunsch"];
  return (
    <main id="main" className="content">
      <PageHead eyebrow={t("Hilfe & Support", "Help & support")} title={t("Neues Ticket", "New ticket")} />
      <form className="panel stack" onSubmit={senden}>
        {meldung.data && <p className="notice">{t("Zur gemeldeten Fehlermeldung: ", "For the reported problem: ")}<strong>{meldung.data.message}</strong></p>}
        <fieldset className="stack">
          <legend>{t("Worum geht es?", "What is it about?")}</legend>
          <div className="row">
            {kategorien.map((k) => (
              <label key={k} className="row tk-check"><input type="radio" name="kategorie" checked={kategorie === k} onChange={() => setKategorie(k)} /><span>{kategorieLabel(k, t)}</span></label>
            ))}
          </div>
        </fieldset>
        <label>{t("Betreff", "Subject")}<input value={betreffWert} maxLength={160} onChange={(e) => setBetreff(e.target.value)} required minLength={3} /></label>
        <label>{t("Beschreibung", "Description")}
          <textarea rows={6} maxLength={10000} value={text} onChange={(e) => setText(e.target.value)} required minLength={10}
            placeholder={t("Was ist passiert, was hast du erwartet, und wo im Portal?", "What happened, what did you expect, and where in the portal?")} />
        </label>
        {vorschlaege.length > 0 && (
          <div className="notice stack" role="status" aria-live="polite">
            <strong>{t("Vielleicht hilft dir das schon:", "Maybe this already helps:")}</strong>
            <ul className="tk-liste-schlicht">
              {vorschlaege.map((a) => (
                <li key={a.id}><ArtikelLink a={a} /> <small>{a.text}</small></li>
              ))}
            </ul>
          </div>
        )}
        <Anhaenge orgId={ws.org.org_id} value={anhaenge} onChange={setAnhaenge} />
        {error && <p className="tk-error" role="alert">{error}</p>}
        <div className="row">
          <button type="submit" className="primary" disabled={!!busy || betreffWert.trim().length < 3 || text.trim().length < 10} aria-busy={busy === "ticket"}>
            {busy === "ticket" ? t("Wird gesendet …", "Sending …") : t("Ticket senden", "Send ticket")}</button>
          <Link className="btn" to="/agentur/hilfe">{t("Abbrechen", "Cancel")}</Link>
        </div>
      </form>
    </main>
  );
}

function AnhangLink({ a }: { a: Anhang }) {
  const [busy, setBusy] = useState(false);
  const oeffnen = async () => {
    setBusy(true);
    try {
      const blob = await api.download("support", a.path);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } finally {
      setBusy(false);
    }
  };
  return <button type="button" className="quiet" aria-busy={busy} onClick={() => void oeffnen()}>{a.name} <small>{groesse(a.size)}</small></button>;
}

export function TicketDetail() {
  const ws = useStaffWorkspace();
  const { id = "" } = useParams();
  const q = useQuery({ queryKey: ["support_ticket", id], queryFn: () => loadTicket(api, id) });
  const [antwort, setAntwort] = useState("");
  const [anhaenge, setAnhaenge] = useState<Anhang[]>([]);
  const { busy, error, run } = useAction();
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending) return <Loading />;
  const { ticket, messages, events } = q.data;
  if (!ticket) return <ErrorState error={new Error(t("Ticket nicht gefunden.", "Ticket not found."))} />;
  // 0300/K-424: nach 3 Tagen ohne Antwort automatisch geschlossen → eine Antwort hier öffnet es wieder; von Hand geschlossen bleibt zu
  const letztesStatusEreignis = [...events].reverse().find((e) => e.kind === "status" || e.kind === "auto_geschlossen" || e.kind === "wieder_geoeffnet");
  const autoZu = ticket.status === "geschlossen" && letztesStatusEreignis?.kind === "auto_geschlossen";
  const zu = ticket.status === "geschlossen" && !autoZu;
  return (
    <main id="main" className="content">
      <PageHead eyebrow={t("Hilfe & Support", "Help & support")} title={`${ticketNummer(ticket.number)} ${ticket.subject}`}>
        <div className="row">
          <PillView pill={ticketStatusPill(ticket.status, t)} />
          <span>{kategorieLabel(ticket.category, t)}</span>
          {!zu && !autoZu && ticket.status !== "geloest" && (
            <button type="button" disabled={!!busy} onClick={() => void run("loesen", () => ticketLoesen(api, ticket.id), t("Als gelöst markiert.", "Marked as solved."))}>
              {t("Als gelöst markieren", "Mark as solved")}</button>
          )}
        </div>
      </PageHead>
      <section className="stack" aria-label={t("Verlauf", "History")}>
        {messages.map((m) => (
          <article key={m.id} className={`panel stack tk-ticket-nachricht ${m.side === "support" ? "tk-ticket-support" : ""}`}>
            <small>{m.side === "support" ? t("CreatorOffice-Support", "CreatorOffice support") : t("Du", "You")} · {date(m.created_at)} {time(m.created_at)}</small>
            <p className="tk-vorformatiert">{m.body}</p>
            {m.attachments.length > 0 && <div className="row">{m.attachments.map((a) => <AnhangLink key={a.path} a={a} />)}</div>}
          </article>
        ))}
      </section>
      {zu ? <p className="notice">{t("Dieses Ticket ist geschlossen. Für ein neues Anliegen lege bitte ein neues Ticket an.", "This ticket is closed. Please create a new ticket for a new request.")}</p> : (
        <form className="panel stack" onSubmit={(e) => {
          e.preventDefault();
          void run("antwort", () => ticketAntworten(api, ticket.id, antwort, anhaenge), t("Antwort gesendet.", "Reply sent.")).then((r) => { if (r !== undefined) { setAntwort(""); setAnhaenge([]); } });
        }}>
          {autoZu && <p className="notice" role="status">{t("Wir haben dein Anliegen nach 3 Tagen ohne Antwort geschlossen. Schreib einfach hier, dann ist es wieder offen.", "We closed your request after 3 days without a reply. Just write here and it will be open again.")}</p>}
          <label>{t("Antwort", "Reply")}<textarea rows={4} maxLength={10000} value={antwort} onChange={(e) => setAntwort(e.target.value)} /></label>
          <Anhaenge orgId={ws.org.org_id} value={anhaenge} onChange={setAnhaenge} />
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button type="submit" className="primary" disabled={!!busy || !antwort.trim()} aria-busy={busy === "antwort"}>{busy === "antwort" ? t("Wird gesendet …", "Sending …") : t("Antworten", "Reply")}</button>
            <Link className="btn" to="/agentur/hilfe">{t("Zur Übersicht", "Back to overview")}</Link>
          </div>
        </form>
      )}
    </main>
  );
}

/** K-469: sprechender Name für „Öffnen“ (Vorlesen, Linkliste): der Anfang des Schritts, höchstens 60 Zeichen */
function schrittZiel(text: string, t: (de: string, en: string) => string): string {
  const kurz = text.replace(/\s+/g, " ").trim();
  const satz = kurz.length > 60 ? `${kurz.slice(0, 57).replace(/\s+\S*$/, "")} …` : kurz;
  return `${t("Öffnen", "Open")}: ${satz}`;
}

/** Rundgang mit Coco (20.10.2026): jederzeit neu starten; die Seitenhilfe wieder einschalten, wenn sie aus ist oder schon gesehen wurde */
function CocoHilfe({ userId }: { userId: string | null }) {
  const ws = useStaffWorkspace();
  const { onboarding, save } = useOnboardingPrefs(userId);
  const rolle = rolleFuer(ws);
  const ui = cocoUiText(t);
  const gesehen = !!onboarding.intro_off || Object.entries(onboarding.guides ?? {}).some(([k, v]) => v && k.startsWith("agentur_"));
  // K-557: den Abschnitt nur zeigen, wenn es einen Knopf gibt (Rundgang starten oder Seitenhilfe wieder einschalten)
  if (!rolle && !gesehen) return null;
  return (
    <section className="panel stack tk-coco-hilfe" aria-labelledby="coco-hilfe-titel">
      <div className="tk-guide-kopf">
        <Coco pose="kopf" size={40} />
        <h2 id="coco-hilfe-titel">{ui.hilfeTitel}</h2>
      </div>
      <p>{ui.hilfeText}</p>
      <div className="row">
        {/* D-2: gefüllt ist auf dieser Seite „Neues Ticket“ */}
        {rolle && <button type="button" onClick={() => rundgangStarten(rolle)}>{ui.rundgangStarten}</button>}
        {gesehen && (
          <button type="button" onClick={() => void save({ guides: {}, intro_off: false }).then(() => toast(ui.seitenhilfeAnGemacht)).catch(() => undefined)}>{ui.seitenhilfeAn}</button>
        )}
      </div>
    </section>
  );
}
